import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { Expense } from '../../../models/expense';
import Title from '../../../components/Title';
import { NavLink } from 'react-router-dom';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { useTheme, useMediaQuery, Tooltip, Button, Box,Card, CardContent } from '@mui/material';
import useAxiosInterceptor from '../../AxiosInterceptor';
import HeaderResponsive from '../../../components/layouts/HeaderResponsive';
import StickyListToolbar, {
    stickyListRegionWidth,
} from '../../../components/layouts/StickyListToolbar';
import EmptyStateMessage from '../../../components/layouts/EmptyStateMessage';
import SimpleCustomTable from '../../../components/table/SimpleCustomTable';
import {
    iconActionColumnSizing,
    simpleTableIconButtonSx,
} from '../../../components/table/iconActionColumnSizing';
import { SimpleColumn, SimpleRow, RowId } from '../../../components/table/interface';
import { useDrawer } from '../../../components/context/DrawerContext';
import PermissionButton from '../../../components/common/PermissionButton';
import { useResourcePermissions } from '../../../hooks/useResourcePermissions';

interface ShowAlerts {
    showAlert: (message: string, severity?: 'success' | 'info' | 'warning' | 'error') => void;
    showLoading: () => void;
    hideLoading: () => void;
}

const TipoGasto: React.FC<ShowAlerts> = ({ showAlert, showLoading, hideLoading }) => {
    document.title = "Administrar Tipo Gastos";
    useAxiosInterceptor();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [expenses, setExpenses] = useState<Expense[]>([]);
    const { canCreate, canUpdate, canDelete } = useResourcePermissions('/administrar/tipogasto');
    useEffect(() => {
        const fetchData = async () => {
            showLoading();
            try {
                const { data } = await axios.get('expenses');
                setExpenses(data.data);
                await new Promise((setExpenses) => setTimeout(setExpenses, 1000));
                showAlert('Se ha realizado la consulta exitosamente!', 'info');
            } catch (error) {
                showAlert('Error fetching data!', 'error');
            } finally {
                hideLoading();
            }
        };
        fetchData();
    }, []);
    const { drawerOpen } = useDrawer();

    const simpleColumns: SimpleColumn[] = [
        { field: 'expense', headerName: 'Nombre', flex: 1, minWidth: 180, wrapText: true },
        { field: 'type_expense', headerName: 'Tipo Gasto', flex: 1, minWidth: 150, wrapText: true },
        { field: 'type_operation', headerName: 'Tipo Operación', flex: 1, minWidth: 150 },
        { field: 'type_state', headerName: 'Estado', flex: 1, minWidth: 140 },
        {
            field: 'opt_edit',
            headerName: '',
            minWidth: '55px',
            maxWidth: '55px',
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canUpdate}
                    deniedTooltip="Sin permiso para editar"
                    tooltip="Editar"
                    variant="contained"
                    color="primary"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to={`/administrar/tipogasto/${row.id}/editar`}
                >
                    <EditIcon fontSize="small" />
                </PermissionButton>
            ),
        },
        {
            field: 'opt_delete',
            headerName: '',
            ...iconActionColumnSizing(isMobile),
            align: 'center',
            renderCell: ({ row }: { row: SimpleRow }) => (
                <PermissionButton
                    allowed={canDelete}
                    deniedTooltip="Sin permiso para eliminar"
                    tooltip="Eliminar"
                    variant="contained"
                    color="error"
                    sx={simpleTableIconButtonSx}
                    component={NavLink}
                    to="/administrar/tipogasto"
                >
                    <DeleteIcon fontSize="small" />
                </PermissionButton>
            ),
        },
    ];

    const rows: SimpleRow[] = expenses.map((expense: Expense) => ({
        id: expense.id as RowId,
        expense: expense.name,
        type_expense: expense.type_expense.name,
        type_operation: expense.type_operation.name,
        type_state: expense.type_state.name,
    }));
    return (
        <HeaderResponsive>
            <StickyListToolbar>
                <Title>Información Gastos</Title>
                <Box sx={{ ml: 'auto' }}>
                    <PermissionButton
                        allowed={canCreate}
                        size="small"
                        deniedTooltip="Sin permiso para crear"
                        tooltip="Agregar"
                        variant="contained"
                        startIcon={isMobile ? null : <AddIcon />}
                        component={NavLink}
                        to="/administrar/tipogasto/add"
                    >
                        {isMobile ? <AddIcon /> : 'Agregar'}
                    </PermissionButton>
                </Box>
            </StickyListToolbar>
            <Card>
                <CardContent
                    sx={{
                        width: (theme) => stickyListRegionWidth(theme, drawerOpen),
                        maxWidth: '100%',
                        boxSizing: 'border-box',
                        overflowX: 'auto',
                    }}
                >
                    <Box sx={{ maxHeight: 800, overflowY: 'auto', my: 2 }}>
                        {expenses.length > 0 ? (
                            <SimpleCustomTable
                                rows={rows}
                                columns={simpleColumns}
                                maxHeight={800}
                                rowHeight={24}
                                showBorders
                                showCount
                                exportCsv
                                exportFileName="gastos.csv"
                                wrapCellContent
                                columnGroups={[{ header: 'Opciones', fields: ['opt_edit', 'opt_delete'] }]}
                            />
                        ) : (
                            <EmptyStateMessage message="No existen registros" align="left" />
                        )}
                    </Box>
                </CardContent>
            </Card>
        </HeaderResponsive>
    )
}

export default TipoGasto
